import { useMemo } from "react"

import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@coval/ui"

import { useQueryParams } from "@/hooks/useQueryParams"
import { useSetPageHeader } from "@/hooks/useSetPageHeader"

import type { Cadence } from "../../utils/reportTypes"

interface NotebookPage {
  /** The open notebook's title, or null while a new one is being set up. */
  title: string | null
  /** Cadences of the saved schedules that repeat this notebook, if any. */
  repeats: Cadence[]
}

const CADENCES: Cadence[] = ["daily", "weekly", "monthly"]

/**
 * Names the builder in the app's top bar after what is on it, "New notebook"
 * or the notebook's title, instead of the route segment.
 */
export function NotebookPageHeader({
  organizationSlug,
  workspaceId,
  title,
  repeats,
}: NotebookPage & { organizationSlug: string; workspaceId: string }) {
  const queryParams = useQueryParams()
  const notebooksHref = queryParams.getHref(
    "/home/notebooks",
    organizationSlug,
    workspaceId,
  )
  const label = title ?? "New notebook"
  const cadences = CADENCES.filter((cadence) => repeats.includes(cadence))
  const repeatLabel = cadences.length
    ? `Repeats ${new Intl.ListFormat("en", { type: "conjunction" }).format(cadences)}`
    : null
  const header = useMemo(
    () => (
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Breadcrumb className="min-w-0">
          <BreadcrumbList className="flex-nowrap">
            <BreadcrumbItem>
              <BreadcrumbLink href={notebooksHref}>Notebooks</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="min-w-0">
              <BreadcrumbPage className="truncate" title={label}>
                {label}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {repeatLabel && <Badge>{repeatLabel}</Badge>}
      </div>
    ),
    [label, notebooksHref, repeatLabel],
  )
  useSetPageHeader(header)
  return null
}
